import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { DraftScope, draftKey, requestEditorNavigation } from '../editorDraft';
import { useEditorDraft } from '../hooks/useEditorDraft';
import { QuoteDraftState, quoteDraftSource, validQuoteDraft } from '../quoteDraft';
import { QuoteDraftNotice } from './QuoteDraftNotice';
import { Quote, Product, PipelineItem, LineItem, Client, Logistics, Attachment, Company } from '../types';
import { Button } from './ui/Button';
import { GlassCard } from './ui/GlassCard';
import { Plus, Trash2, Clock, MapPin, User, DollarSign, Save, X, Loader2, Sparkles, Box, Building2, Check, Eye, ThumbsUp, ThumbsDown, Send, Users, Calculator, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '../supabaseClient';
import {ConversationalSummary} from './ConversationalSummary';
import {ConversationSession} from '../conversationState';
import {summaryContext} from '../summaryContext';
import {generateConversationalSummary} from '../services/textGeneration';
import {RewriteImprove} from './RewriteImprove';
import {proposalRewriteContext} from '../rewriteContext';
import {proposalContext} from '../proposalContext';
import { motion, AnimatePresence } from 'framer-motion'; // ⚡ ADD THIS LINE HERE
import { calculationPatch, isAcceptedQuote, linePrice, parseQuoteObject, quoteHours, quoteLabour, quotePricing, quoteStaffing, requiresCalculationReview, staffingPolicy, StaffingMode, withStaffingPolicy } from '../quoteCalculations';

export type QuoteStatus = 'DRAFT' | 'SENT' | 'APPROVED' | 'REJECTED';

interface QuoteBuilderProps {
  initialQuote: Quote | null;
  linkedLead: PipelineItem | null | undefined;
  onSave: (quote: Quote, shouldPreview?: boolean, onPersisted?: (quote: Quote) => void) => void | boolean | Promise<void | boolean>;
  draftScope?: DraftScope;
  serverQuote?: Quote | null;
  checkLatestQuote?: (quoteId: string) => Promise<Quote | null>;
  isPersistedQuote?: boolean;
  onCancel: () => void;
  availableProducts: Product[];
  companies?: Company[];
  onUpdateStatus?: (quoteId: string, leadId: string | undefined, newStatus: QuoteStatus) => Promise<void>;
}

// ⚡ BULLETPROOF NUMBER PARSER
const safeNum = (val: any): number => {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    const cleaned = String(val).replace(/[^0-9.-]+/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

export const QuoteBuilder: React.FC<QuoteBuilderProps> = ({ 
  initialQuote: requestedQuote,
  linkedLead, 
  onSave, 
  onCancel, 
  availableProducts,
  onUpdateStatus,
  companies = [],
  draftScope,
  serverQuote,
  checkLatestQuote,
  isPersistedQuote,
}) => {
  const wasSaved = useRef(isPersistedQuote ?? !!requestedQuote);
  const [checkedQuote, setCheckedQuote] = useState<Quote | null>(null);
  const initialQuote = checkedQuote || serverQuote || requestedQuote;
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [setupTime, setSetupTime] = useState('');
  const [packDownTime, setPackDownTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [customSlides, setCustomSlides] = useState<any[]>([]);
  
  const [presentationContent, setPresentationContent] = useState('');

  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [conversationSession,setConversationSession]=useState<ConversationSession|null>(null);
  const conversationEditorId=useRef(Math.random().toString(36).slice(2));
  
  // ⚡ NEW TEMPLATE STATES
  const [baseTemplates, setBaseTemplates] = useState<any[]>([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  
  const [currentQuoteId, setCurrentQuoteId] = useState<string | null>(initialQuote?.id || null);

  // ⚡ FETCH TEMPLATES ON LOAD
  useEffect(() => {
      const fetchTemplates = async () => {
          const { data } = await supabase.from('quotes').select('*').eq('is_base_template', true).order('template_name');
          if (data) setBaseTemplates(data);
      };
      fetchTemplates();
  }, []);
  const [currentLeadId, setCurrentLeadId] = useState<string | null>(null);

  const [staffingTier, setStaffingTier] = useState('DRY_HIRE');
  const [staffCountRequired, setStaffCountRequired] = useState(0);
  const [staffingMode, setStaffingMode] = useState<StaffingMode>('LINE_TOTAL');
  const [calculationReviewed, setCalculationReviewed] = useState(!requiresCalculationReview(initialQuote));
  const acceptedQuote = isAcceptedQuote(initialQuote);
  
  const [client, setClient] = useState<Client & { accountsEmail?: string }>({
    firstName: '', lastName: '', company: '', companyId: '', email: '', phone: '', accountsEmail: ''
  });

  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  const [items, setItems] = useState<LineItem[]>([]);
  
  const [logistics, setLogistics] = useState<Logistics>({
    hours: 0, hourlyRate: 0, travelDistanceKm: 0, travelRatePerKm: 0, setupFee: 0
  });

  const [showProfitAdvisor, setShowProfitAdvisor] = useState(false);
  const [internalCosts, setInternalCosts] = useState({
      laborRatePerHour: 35, 
      designPreProduction: 0,
      logisticsTravel: 0,
      physicalMaterials: 0,
      digitalSoftware: 0
  });

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [packingList, setPackingList] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hydratedSource, setHydratedSource] = useState<string | null>(null);
  const source = quoteDraftSource(initialQuote, linkedLead);
  const value: QuoteDraftState = { title, eventDate, eventStartTime, eventEndTime, setupTime, packDownTime, eventLocation,
    isTemplate, customSlides, presentationContent, currentQuoteId, currentLeadId, staffingTier, staffCountRequired,
    staffingMode, calculationReviewed, client, companySearch, items, logistics, internalCosts, attachments, packingList, persistedQuote: wasSaved.current };
  const restoreDraft = useCallback((data: QuoteDraftState) => {
    setTitle(data.title); setEventDate(data.eventDate); setEventStartTime(data.eventStartTime); setEventEndTime(data.eventEndTime);
    setSetupTime(data.setupTime); setPackDownTime(data.packDownTime); setEventLocation(data.eventLocation);
    setIsTemplate(data.isTemplate); setCustomSlides(data.customSlides); setPresentationContent(data.presentationContent);
    setCurrentQuoteId(data.currentQuoteId); setCurrentLeadId(data.currentLeadId); setStaffingTier(data.staffingTier);
    setStaffCountRequired(data.staffCountRequired); setStaffingMode(data.staffingMode); setCalculationReviewed(data.calculationReviewed);
    setClient(data.client); setCompanySearch(data.companySearch); setItems(data.items); setLogistics(data.logistics);
    setInternalCosts(data.internalCosts); setAttachments(data.attachments); setPackingList(data.packingList);
  }, []);
  const draft = useEditorDraft({ storageKey: draftScope ? draftKey(draftScope, requestedQuote?.id) : null,
    source, ready: hydratedSource, value, restore: restoreDraft, validate: validQuoteDraft, busy: isSaving });
  const hydration = useRef<{ source: string; reset: number } | null>(null);
  const cancel = () => requestEditorNavigation(onCancel);
  const saveLock = useRef(false);
  const runProtectedSave = async (action: () => Promise<void>) => {
    if (saveLock.current || !canSaveCalculations()) return;
    saveLock.current = true; setIsSaving(true);
    try { await action(); } finally { saveLock.current = false; setIsSaving(false); }
  };
  const checkSavedBaseline = async () => {
    draft.flush();
    const id = currentQuoteId || requestedQuote?.id;
    if (!checkLatestQuote || !id) return true;
    try {
      const latest = await checkLatestQuote(id);
      if (!latest && !wasSaved.current) return true;
      if (!latest) { alert('This saved Quote is no longer available. Save is blocked; your local draft is retained.'); return false; }
      if (quoteDraftSource(latest, linkedLead) !== source) { setCheckedQuote(latest); return false; }
      return true;
    } catch {
      alert('Could not verify the latest saved Quote. Save is blocked; your local draft is retained.'); return false;
    }
  };

  const calculateShiftHours = () => {
    return quoteHours(setupTime, packDownTime);
  };

  const calculationSource = () => ({
    items, staff_count_required: staffCountRequired, staffing_tier: staffingTier,
    logistics: withStaffingPolicy(logistics, staffingMode), setupTime, packDownTime, internal_costs: internalCosts,
  });
  const canSaveCalculations = () => {
    if (draft.blocked) { alert('Resolve the local draft or saved-data conflict before saving.'); return false; }
    if (acceptedQuote) { alert('Accepted Quote totals are preserved. Duplicate to a draft revision before changing calculations.'); return false; }
    if (!calculationReviewed) { alert('Review the staffing mode and percentage-discount totals, then acknowledge the calculation review.'); return false; }
    return true;
  };

  const calculateTotals = () => {
    const included = items.filter(i => !i.isOptional || (i as any).simulateSelected || (i as any).selected);
    const pricing = quotePricing(items.filter(i => !i.isOptional));
    const itemsTotal = pricing.subtotal;

    // ⚡ ADD THIS LINE TO FIX THE ERROR
    const logisticsCost = 0;

    // ⚡ KILLED LOGISTICS MATH: Subtotal is now purely based on your line items!
    const subtotal = itemsTotal; 
    const grandTotal = pricing.total;

    const totalShiftHours = calculateShiftHours();
    
    const labour = quoteLabour(calculationSource(), included);
    const dynamicStaffCount = quoteStaffing(calculationSource()).total;
    const simulatedStaffCount = labour.staff;
    const autoLaborCost = labour.cost;
    
    const totalInternalCost = autoLaborCost + 
                              safeNum(internalCosts.designPreProduction) + 
                              safeNum(internalCosts.logisticsTravel) + 
                              safeNum(internalCosts.physicalMaterials) + 
                              safeNum(internalCosts.digitalSoftware);

    const simulatedRevenue = quotePricing(included).subtotal;
    const grossProfit = simulatedRevenue - totalInternalCost;
    const profitMarginPercent = simulatedRevenue > 0 ? (grossProfit / simulatedRevenue) * 100 : 0;

    return { 
        subtotal, grandTotal, totalInternalCost, grossProfit,
        profitMarginPercent, autoLaborCost, totalShiftHours, logisticsCost, dynamicStaffCount, simulatedStaffCount
    };
  };

  const totals = calculateTotals();

  const timeOptions = useMemo(() => {
    const options = [];
    for (let h = 6; h < 30; h++) {
      for (let m = 0; m < 60; m += 15) {
        const actualHour = h % 24;
        const hour24 = actualHour.toString().padStart(2, '0');
        const min = m.toString().padStart(2, '0');
        const val = `${hour24}:${min}`;
        
        const ampm = actualHour >= 12 ? 'PM' : 'AM';
        const hour12 = actualHour % 12 || 12;
        const isNextDay = h >= 24;
        
        const label = `${hour12.toString().padStart(2, '0')}:${min} ${ampm} ${isNextDay ? '(Next Day)' : ''}`;
        options.push({ value: val, label, sortValue: h * 60 + m });
      }
    }
    return options;
  }, []);

  const getFilteredTimes = (minTimeString?: string) => {
    if (!minTimeString) return timeOptions;
    const minOption = timeOptions.find(o => o.value === minTimeString);
    const minSortValue = minOption ? minOption.sortValue : 0;
    return timeOptions.filter(o => o.sortValue >= minSortValue);
  };

  useLayoutEffect(() => {
    if (!draft.canHydrate || (hydration.current?.source === source && hydration.current?.reset === draft.reset)) return;
    hydration.current = { source, reset: draft.reset };
    // Reset only for a changed authoritative source (or explicit discard), never a new object reference.
    restoreDraft({ title: '', eventDate: '', eventStartTime: '', eventEndTime: '', setupTime: '', packDownTime: '', eventLocation: '',
      isTemplate: false, customSlides: [], presentationContent: '', currentQuoteId: null, currentLeadId: null, staffingTier: 'DRY_HIRE',
      staffCountRequired: 0, staffingMode: 'LINE_TOTAL', calculationReviewed: !requiresCalculationReview(initialQuote),
      client: { firstName: '', lastName: '', company: '', companyId: '', email: '', phone: '', accountsEmail: '' }, companySearch: '',
      items: [], logistics: { hours: 0, hourlyRate: 0, travelDistanceKm: 0, travelRatePerKm: 0, setupFee: 0 },
      internalCosts: { laborRatePerHour: 35, designPreProduction: 0, logisticsTravel: 0, physicalMaterials: 0, digitalSoftware: 0 }, attachments: [], packingList: [], persistedQuote: wasSaved.current });
    if (initialQuote) {
      setTitle(initialQuote?.title || '');
      setEventDate(initialQuote?.eventDate || '');
      setEventStartTime(initialQuote?.eventStartTime?.slice(0,5) || '');
      setEventEndTime(initialQuote?.eventEndTime?.slice(0,5) || '');
      setSetupTime(initialQuote?.setupTime?.slice(0,5) || '');
      setPackDownTime(initialQuote?.packDownTime?.slice(0,5) || '');
      
      // ⚡ FIX 1: Safely parse items FIRST so the Ghost Buster can use them
      let safeItems = initialQuote?.items || [];
      if (typeof safeItems === 'string') { try { safeItems = JSON.parse(safeItems); } catch(e) { safeItems = []; } }
      setItems(safeItems);

      // ⚡ FIX 2: THE GHOST BUSTER ENGINE
      // Scans the loaded list and removes any gear that is already automated by the products!
      let parsedPacking = initialQuote.packingList || [];
      if (typeof parsedPacking === 'string') { try { parsedPacking = JSON.parse(parsedPacking); } catch(e) { parsedPacking = []; } }
      
      let manualPackingOnly = [...parsedPacking];
      safeItems.forEach((item: any) => {
          const product = availableProducts.find(p => String(p.id) === String(item.productId || item.product_id)) as any;
          if (product) {
              const eqList = product.equipment_list || product.equipmentList || [];
              const gearToRemove = eqList.length > 0 ? eqList : [product.name]; // Fallback to product name
              manualPackingOnly = manualPackingOnly.filter(gear => !gearToRemove.includes(gear));
          }
      });
      setPackingList(manualPackingOnly);
      
      setEventLocation(initialQuote?.eventLocation || '');
      setPresentationContent(initialQuote.presentationSlideContent || '');
      
      let parsedSlides = (initialQuote as any).custom_slides || (initialQuote as any).customSlides || [];
      if (typeof parsedSlides === 'string') { try { parsedSlides = JSON.parse(parsedSlides); } catch(e) { parsedSlides = []; } }
      setCustomSlides(parsedSlides);
      
      setStaffingTier((initialQuote as any).staffing_tier || (initialQuote as any).staffingTier || 'DRY_HIRE');
      setStaffCountRequired(quoteStaffing(initialQuote).savedTotal);
      setStaffingMode(staffingPolicy(initialQuote)?.mode ?? (quoteStaffing(initialQuote).savedTotal > 0 ? 'EXPLICIT_TOTAL' : 'LINE_TOTAL'));
      setCalculationReviewed(!requiresCalculationReview(initialQuote));

      if (initialQuote?.client) {
          setClient({
              ...initialQuote.client,
              accountsEmail: (initialQuote as any).accounts_email || (initialQuote.client as any).accountsEmail || ''
          });
          if (initialQuote.client.company) setCompanySearch(initialQuote.client.company);
      }
      
      if (initialQuote?.logistics) {
          let safeLogistics = initialQuote.logistics;
          if (typeof safeLogistics === 'string') { try { safeLogistics = JSON.parse(safeLogistics); } catch(e) { safeLogistics = {} as any; } }
          
          // ⚡ Strict assignment so 0 doesn't get ignored
          setLogistics({
              hours: 0, hourlyRate: 0,
              travelDistanceKm: safeLogistics.travelDistanceKm !== undefined ? Number(safeLogistics.travelDistanceKm) : 0,
              travelRatePerKm: safeLogistics.travelRatePerKm !== undefined ? Number(safeLogistics.travelRatePerKm) : 0,
              setupFee: safeLogistics.setupFee !== undefined ? Number(safeLogistics.setupFee) : 0,
              // ⚡ ADD THIS LINE:
              roadshowSchedule: safeLogistics.roadshowSchedule || ''
          });
      }

      const dbCosts = parseQuoteObject((initialQuote as any).internal_costs ?? parseQuoteObject(initialQuote.logistics).internalCosts);
      
      if (dbCosts) {
          setInternalCosts({
              laborRatePerHour: safeNum(dbCosts.laborRatePerHour ?? 35),
              designPreProduction: safeNum(dbCosts.designPreProduction),
              logisticsTravel: safeNum(dbCosts.logisticsTravel),
              physicalMaterials: safeNum(dbCosts.physicalMaterials),
              digitalSoftware: safeNum(dbCosts.digitalSoftware)
          });
      }

      setAttachments(initialQuote?.attachments || []);
      setCurrentQuoteId(initialQuote?.id || null);
      setCurrentLeadId(initialQuote?.linkedCrmItemId || (initialQuote as any)?.lead_id || null);
      
    } else if (linkedLead) {
        setTitle(linkedLead.eventName || '');
        setEventDate(linkedLead.eventDate?.split('T')[0] || '');
        setEventLocation((linkedLead as any).location || (linkedLead as any).venue_location || linkedLead.eventVenue || '');
        if (linkedLead.companyName) setCompanySearch(linkedLead.companyName);
    }
    setHydratedSource(`${source}:${draft.reset}`);
  }, [draft.canHydrate, draft.reset, source, initialQuote, linkedLead, availableProducts, restoreDraft]);

  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target as Node) && 
              companyInputRef.current && !companyInputRef.current.contains(event.target as Node)) {
              setShowCompanyDropdown(false);
          }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusChange = (newStatus: QuoteStatus) => runProtectedSave(() => performStatusChange(newStatus));
  const performStatusChange = async (newStatus: QuoteStatus) => {
    if (!currentQuoteId) return;
    if (!canSaveCalculations()) return;
    // Existing approval writes only its existing subset of fields, not the whole editor.
    // Require an explicit Save first rather than clearing an unsaved introduction/client draft.
    if (newStatus === 'APPROVED' && draft.dirty) { alert('Save your unsaved Quote changes before Approval. Approval does not save every editor field.'); return; }
    if (!(await checkSavedBaseline())) return;

    let lostReason = "";
    if (newStatus === 'REJECTED') {
      const reason = window.prompt("Why was this quote rejected?");
      if (reason === null) return; 
      lostReason = reason || "No reason provided";
    }

    setIsSaving(true);
    const calculated = calculationPatch(calculationSource());
    const { subtotal, grand_total: grandTotal } = calculated;

    try {
      let updatePayload: any = { 
        status: newStatus,
        title: newStatus === 'REJECTED' ? `${title} (LOST: ${lostReason})` : title,
        subtotal: subtotal, 
        grand_total: grandTotal,
        total_amount: grandTotal,
        updated_at: new Date().toISOString()
      };
      Object.assign(updatePayload, calculated);

      // ⚡ UPGRADE: Run the Blueprint Engine if Silent Approving!
      if (newStatus === 'APPROVED') {
          const tier1Logistics = [
            { id: 'contact', label: 'On-the-Night Contact Person', type: 'text', required: true, helpText: 'Who is our team\'s main point of contact during the event? (Name & Mobile Number)', section: 'Event Logistics' },
            { id: 'access', label: 'Venue Access & Pack-in Time', type: 'text', required: true, helpText: 'What is the earliest time our crew can access the room to begin setting up?', section: 'Event Logistics' },
            { id: 'runsheet', label: 'Event Run Sheet', type: 'upload', required: false, helpText: 'Please upload your official run sheet or timeline so our team is synced with your schedule.', section: 'Event Logistics' },
            { id: 'floorplan', label: 'Venue Floor Plan / Placement', type: 'upload', required: false, helpText: 'Upload a floor plan or briefly describe exactly where you want the installation located in the room.', section: 'Event Logistics' },
            { id: 'parking', label: 'Loading Dock & Parking Instructions', type: 'textarea', required: false, helpText: 'Are there specific loading bays, height restrictions, or parking passes our crew needs to know about?', section: 'Event Logistics' },
            { id: 'power', label: 'Power & Wi-Fi Availability', type: 'select', options: 'Dedicated Power & Wi-Fi available, Power only (Bring 4G/5G internet), Neither (Fully off-grid needed)', required: true, section: 'Event Logistics' },
            { id: 'wifi_details', label: 'Wi-Fi Network & Password', type: 'text', required: false, helpText: 'If dedicated venue Wi-Fi is available, please provide the login details here.', section: 'Event Logistics' },
            { id: 'meals', label: 'Contractor Meals', type: 'select', options: 'Yes - Meals provided, No - Crew to sort own meals', required: true, helpText: 'For events spanning over 4 hours (including setup/packdown), it is industry standard to provide a vendor meal.', section: 'Event Logistics' },
            { id: 'notes', label: 'Additional Production Notes', type: 'textarea', required: false, helpText: 'Anything else our crew should know before arriving?', section: 'Event Logistics' }
          ];

          const tier2Sharing = [
            { id: 'sharing_method', label: 'Preferred Sharing Method', type: 'select', options: 'Email & SMS, Email Only, SMS Only, QR Code Only, AirDrop Only (Apple devices)', required: true, section: 'Digital Sharing' },
            { id: 'email_from_name', label: 'Email From Name', type: 'text', required: false, helpText: 'e.g., ABC Company or ASB Gala', section: 'Digital Sharing' },
            { id: 'email_reply_to', label: 'Reply Email Address', type: 'text', required: false, helpText: 'Where should replies go? e.g., events@abccompany.com', section: 'Digital Sharing' },
            { id: 'email_subject', label: 'Email Subject Line', type: 'text', required: false, helpText: 'Keep it punchy! (e.g., "Your incredible photos from the ASB Gala!")', section: 'Digital Sharing' },
            { id: 'email_body', label: 'Email Body Copy', type: 'textarea', required: false, helpText: 'The message inside the email. Great place for a thank you note or sponsor links.', section: 'Digital Sharing' },
            { id: 'sms_copy', label: 'SMS / Text Message Copy', type: 'textarea', required: false, helpText: 'Keep it under 140 characters. Example: "Thanks for celebrating with us! View your Quikpix gallery here: [Link]"', section: 'Digital Sharing' },
            { id: 'hashtag', label: 'Event Hashtag', type: 'text', required: false, helpText: 'e.g., #ASBGala2026. We will pre-populate this when guests share to social media.', section: 'Digital Sharing' }
          ];

          let needsDigitalSharing = false;
          let tier3Products: any[] = [];
          const qTitle = (title || '').toLowerCase();

          items.forEach(item => {
            // ⚡ FIX: We removed the 'isOptional' skip so Silent Approve scans ALL items!
            const iName = (item.name || '').toLowerCase();
            const iDesc = (item.description || '').toLowerCase();
            const sharingKeywords = ['booth', '360', 'ai', 'roamer', 'roaming', 'glamour', 'sharing', 'digital', 'kiosk', 'photo'];
            
            if (sharingKeywords.some(keyword => iName.includes(keyword) || iDesc.includes(keyword) || qTitle.includes(keyword))) {
                needsDigitalSharing = true;
            }

            const product = availableProducts.find(p => String(p.id) === String(item.productId));
            if (product) {
                const fields = (product as any).onboarding_fields || product.onboardingFields || [];
                if (fields.length > 0) {
                    fields.forEach((f: any) => {
                        const fLabel = (f.label || '').toLowerCase();
                        if (!fLabel.includes('email') && !fLabel.includes('sms') && !fLabel.includes('subject line') && !fLabel.includes('sharing') && !fLabel.includes('wi-fi') && !fLabel.includes('wifi')) {
                            tier3Products.push({ ...f, section: `${product.name} Specs` });
                        }
                    });
                }
            }
          });

          updatePayload.portal_blueprint = [
              ...tier1Logistics,
              ...(needsDigitalSharing ? tier2Sharing : []),
              ...tier3Products
          ];
      }

      const { error: quoteError } = await supabase
        .from('quotes')
        .update(updatePayload)
        .eq('id', currentQuoteId);

      if (quoteError) throw quoteError;

      const { data: quoteCheck } = await supabase.from('quotes').select('lead_id').eq('id', currentQuoteId).single();
      const leadId = quoteCheck?.lead_id || currentLeadId || linkedLead?.id;

      if (leadId) {
        let pipelineStatus = 'PROPOSAL';
        if (newStatus === 'REJECTED') pipelineStatus = 'LOST';
        if (newStatus === 'APPROVED') pipelineStatus = 'PRE_PRODUCTION';

        const { error: leadError } = await supabase.from('event_leads').update({ status: pipelineStatus, value: subtotal }).eq('id', leadId);
        if (leadError) console.error("Pipeline Update Failed:", leadError.message);
      }

      if (onUpdateStatus) {
          await onUpdateStatus(currentQuoteId, leadId, newStatus);
      }
      
      if (newStatus === 'APPROVED') draft.saved();
      onCancel();
    } catch (err: any) {
      console.error("Critical Update Error:", err);
      alert(`Update failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const addItem = () => {
    const newItem: LineItem = { id: Math.random().toString(36).substr(2, 9), name: 'New Service', description: '', price: 0, quantity: 1, discount: 0, isOptional: false };
    setItems(prev => [...prev, newItem]);
  };

  const updateItem = (id: string, updates: Partial<LineItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const moveGearItem = (index: number, direction: 'left' | 'right') => {
    if (direction === 'left' && index === 0) return;
    if (direction === 'right' && index === packingList.length - 1) return;
    setPackingList(prev => {
        const newList = [...prev];
        const swapIndex = direction === 'left' ? index - 1 : index + 1;
        [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
        return newList;
    });
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return; 
    if (direction === 'down' && index === items.length - 1) return; 
    setItems(prev => {
        const newItems = [...prev];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
        return newItems;
    });
  };

  const addProductItem = (product: Product) => {
    const newItem: LineItem = {
      id: Math.random().toString(36).substr(2, 9),
      productId: product.id, name: product.name, description: product.description,
      price: product.price || 0, quantity: 1, discount: 0, isOptional: false
    };
    setItems(prev => [...prev, newItem]);
  };

  const handleCompanySelect = (company: Company) => {
      setClient(prev => ({ 
          ...prev, 
          company: company.name, 
          companyId: company.id, 
          accountsEmail: (company as any).accounts_email || '' 
      }));
      setCompanySearch(company.name);
      setShowCompanyDropdown(false);
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setCompanySearch(val);
      setClient(prev => ({ ...prev, company: val, companyId: '' }));
      setShowCompanyDropdown(true);
  };

  // ⚡ NEW: LOAD TEMPLATE FUNCTION
  const handleLoadTemplate = (templateId: string) => {
      const template = baseTemplates.find(t => t.id === templateId);
      if (!template) return;

      if (!window.confirm(`Overwrite current items and pricing with the "${template.template_name}" template?`)) return;

      let safeItems = template.items || [];
      if (typeof safeItems === 'string') { try { safeItems = JSON.parse(safeItems); } catch(e) { safeItems = []; } }
      // Give them fresh IDs so they don't clash with the master template
      const clonedItems = safeItems.map((item: any) => ({ ...item, id: Math.random().toString(36).substr(2, 9) }));
      setItems(clonedItems);

      // ⚡ FIX 3: WIPE MANUAL GEAR ON TEMPLATE LOAD
      // Templates shouldn't carry over custom manual notes to new clients
      setPackingList([]);

      let parsedSlides = template.custom_slides || [];
      if (typeof parsedSlides === 'string') { try { parsedSlides = JSON.parse(parsedSlides); } catch(e) { parsedSlides = []; } }
      setCustomSlides(parsedSlides);

      setPresentationContent(template.presentation_slide_content?.text || (typeof template.presentation_slide_content === 'string' ? template.presentation_slide_content : ''));
      setStaffingTier(template.staffing_tier || 'DRY_HIRE');
      setStaffCountRequired(Number(template.staff_count_required || 0));
      setStaffingMode(staffingPolicy(template)?.mode ?? (quoteStaffing(template).savedTotal > 0 ? 'EXPLICIT_TOTAL' : 'LINE_TOTAL'));
      setCalculationReviewed(!requiresCalculationReview(template));

      const dbCosts = parseQuoteObject(template.internal_costs ?? parseQuoteObject(template.logistics).internalCosts);
      if (dbCosts) {
          setInternalCosts({
              laborRatePerHour: safeNum(dbCosts.laborRatePerHour ?? 35),
              designPreProduction: safeNum(dbCosts.designPreProduction),
              logisticsTravel: safeNum(dbCosts.logisticsTravel),
              physicalMaterials: safeNum(dbCosts.physicalMaterials),
              digitalSoftware: safeNum(dbCosts.digitalSoftware)
          });
      }
  };

  // ⚡ NEW: SAVE AS TEMPLATE FUNCTION (With Overwrite Engine)
  const handleSaveAsBaseTemplate = async () => {
      if (!canSaveCalculations()) return;
      if (!templateForm.name.trim()) return alert("Please provide a Template Name.");
      setIsSaving(true);
      const calculated = calculationPatch(calculationSource());
      const { subtotal, grand_total: grandTotal } = calculated;

      try {
          // ⚡ THE OVERWRITE ENGINE: Check if a template with this exact name already exists
          const existingTemplate = baseTemplates.find(t => t.template_name.toLowerCase() === templateForm.name.toLowerCase().trim());
          const templateId = existingTemplate ? existingTemplate.id : crypto.randomUUID();

          const purchasedItems = items.filter(i => !i.isOptional && i.productId);
          let compiledPackingList = [...packingList];
          purchasedItems.forEach(item => {
              const masterProduct = availableProducts.find(p => p.id === item.productId) as any; 
              if (masterProduct && masterProduct.equipment_list && Array.isArray(masterProduct.equipment_list)) {
                  compiledPackingList.push(...masterProduct.equipment_list);
              }
          });
          compiledPackingList = Array.from(new Set(compiledPackingList));

          const packagedInternalCosts = {
              laborRatePerHour: safeNum(internalCosts.laborRatePerHour),
              designPreProduction: safeNum(internalCosts.designPreProduction),
              logisticsTravel: safeNum(internalCosts.logisticsTravel),
              physicalMaterials: safeNum(internalCosts.physicalMaterials),
              digitalSoftware: safeNum(internalCosts.digitalSoftware)
          };

          const cleanedItems = items.map(item => ({
              id: item.id, name: item.name, description: item.description, 
              price: safeNum(item.price), quantity: safeNum(item.quantity), 
              discount: safeNum(item.discount), productId: item.productId, 
              isOptional: item.isOptional, isCoreChoice: (item as any).isCoreChoice || false,
              staffRequired: safeNum((item as any).staffRequired)
          }));

          const templateData = {
              id: templateId, // ⚡ USE THE EXISTING ID TO OVERWRITE, OR A NEW ONE IF IT DOESN'T EXIST
              title: templateForm.name, 
              template_name: templateForm.name,
              template_description: templateForm.description,
              is_base_template: true,
              status: 'DRAFT',
              subtotal: safeNum(subtotal), grand_total: safeNum(grandTotal), total_amount: safeNum(grandTotal),
              presentation_slide_content: { text: presentationContent }, packing_list: compiledPackingList,
              custom_slides: customSlides, staffing_tier: calculated.staffing_tier, staff_count_required: calculated.staff_count_required,
              setup_fee: 0, travel_distance_km: 0, travel_rate_per_km: 0, logistics_hours: 0, logistics_hourly_rate: 0,
              logistics: JSON.stringify(withStaffingPolicy({}, staffingMode)),
              internal_costs: JSON.stringify(packagedInternalCosts),
              items: JSON.stringify(cleanedItems)
          };

          // ⚡ USE UPSERT TO EITHER CREATE NEW OR OVERWRITE EXISTING
          const { error } = await supabase.from('quotes').upsert([templateData]);
          if (error) throw error;

          alert(`Successfully ${existingTemplate ? 'updated' : 'saved'} "${templateForm.name}" in your Template Library!`);
          setIsTemplateModalOpen(false);
          setTemplateForm({ name: '', description: '' });
          
          const { data } = await supabase.from('quotes').select('*').eq('is_base_template', true).order('template_name');
          if (data) setBaseTemplates(data);

      } catch (err: any) {
          console.error("Save Template Error:", err);
          alert(`Failed to save template: ${err.message}`);
      } finally {
          setIsSaving(false);
      }
  };

  const filteredCompanies = companies.filter(c => 
      c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleSave = (shouldPreview = false) => runProtectedSave(() => performSave(shouldPreview));
  const performSave = async (shouldPreview: boolean) => {
    if (!canSaveCalculations()) return;
    const submittedDraft = draft.captureSave();
    if (!(await checkSavedBaseline())) return;
    if (!eventDate && !isTemplate) {
        alert("Please select an event date before saving.");
        return;
    }
    setIsSaving(true);
    
    const calculated = calculationPatch(calculationSource());
    const { subtotal, grand_total: grandTotal } = calculated;

    try {
        let finalCompanyId = client.companyId;
        const cleanCompanyName = companySearch?.trim();
        
        if (cleanCompanyName && !finalCompanyId) {
            const localMatch = companies?.find(c => c.name.toLowerCase().trim() === cleanCompanyName.toLowerCase());
            if (localMatch) {
                finalCompanyId = localMatch.id;
            } else {
                const { data: dbMatch } = await supabase.from('companies').select('id').ilike('name', cleanCompanyName).maybeSingle();
                if (dbMatch) {
                    finalCompanyId = dbMatch.id;
                } else {
                    const { data: newComp } = await supabase.from('companies').insert([{ name: cleanCompanyName, accounts_email: client.accountsEmail }]).select().single();
                    if (newComp) finalCompanyId = newComp.id;
                }
            }
            setClient(prev => ({ ...prev, companyId: finalCompanyId, company: cleanCompanyName }));
        }

        let finalClientId = currentLeadId; 

        if (!finalClientId) {
            const { data: newLead, error: leadError } = await supabase
                .from('event_leads')
                .insert([{
                    first_name: client.firstName || 'New', last_name: client.lastName || 'Client',
                    company_name: cleanCompanyName, company_id: finalCompanyId || null, email: client.email, phone: client.phone,
                    event_date: eventDate || null, venue_location: eventLocation, project_ref: title || 'Untitled Event', status: 'PROPOSAL',
                    value: subtotal, setup_time: setupTime || null, event_start_time: eventStartTime || null, event_end_time: eventEndTime || null, pack_down_time: packDownTime || null
                }]).select().single();

            if (leadError) throw leadError;
            finalClientId = newLead.id;
            setCurrentLeadId(finalClientId); 
        } else {
            await supabase.from('event_leads').update({
                project_ref: title || 'Untitled Event', event_date: eventDate || null, venue_location: eventLocation, company_name: cleanCompanyName, company_id: finalCompanyId || null,
                value: subtotal, setup_time: setupTime || null, event_start_time: eventStartTime || null, event_end_time: eventEndTime || null, pack_down_time: packDownTime || null
            }).eq('id', finalClientId);
        }
        
        let finalQuoteId = currentQuoteId || crypto.randomUUID();
        setCurrentQuoteId(finalQuoteId);

        const purchasedItems = items.filter(i => !i.isOptional && i.productId);
        let compiledPackingList = [...packingList];
        let compiledTechNotes = "";

        purchasedItems.forEach(item => {
            const masterProduct = availableProducts.find(p => p.id === item.productId) as any; 
            if (masterProduct) {
                if (masterProduct.equipment_list && Array.isArray(masterProduct.equipment_list)) {
                    compiledPackingList.push(...masterProduct.equipment_list);
                } else if (masterProduct.equipmentList && Array.isArray(masterProduct.equipmentList)) {
                    compiledPackingList.push(...masterProduct.equipmentList);
                }
                const notes = masterProduct.production_guide || '';
                if (notes) compiledTechNotes += `\n\n--- [${masterProduct.name}] ---\n${notes}`;
            }
        });

        compiledPackingList = Array.from(new Set(compiledPackingList));

        const packagedInternalCosts = {
            laborRatePerHour: safeNum(internalCosts.laborRatePerHour),
            designPreProduction: safeNum(internalCosts.designPreProduction),
            logisticsTravel: safeNum(internalCosts.logisticsTravel),
            physicalMaterials: safeNum(internalCosts.physicalMaterials),
            digitalSoftware: safeNum(internalCosts.digitalSoftware)
        };

        // ⚡ NUKED LOGISTICS: Hardcoded to zero forever.
        const packagedLogistics = {
            ...parseQuoteObject(calculated.logistics),
            hours: 0, hourlyRate: 0, travelDistanceKm: 0, travelRatePerKm: 0, setupFee: 0,
            internalCosts: packagedInternalCosts,
            // ⚡ ADD THIS LINE:
            roadshowSchedule: logistics.roadshowSchedule || ''
        };

        const cleanedItems = items.map(item => ({
            id: item.id, name: item.name, description: item.description, 
            price: safeNum(item.price), quantity: safeNum(item.quantity), 
            discount: safeNum(item.discount), productId: item.productId, 
            isOptional: item.isOptional,
            isCoreChoice: (item as any).isCoreChoice || false,
            staffRequired: safeNum((item as any).staffRequired)
        }));

        const quoteData: any = {
            id: finalQuoteId, title: title || 'Untitled', status: initialQuote?.status || 'DRAFT',
            client_first_name: client.firstName, client_last_name: client.lastName, client_email: client.email, client_phone: client.phone, client_company: cleanCompanyName,
            accounts_email: client.accountsEmail,
            subtotal: safeNum(subtotal), grand_total: safeNum(grandTotal), event_date: eventDate || null, event_location: eventLocation,
            event_start_time: eventStartTime || null, event_end_time: eventEndTime || null, setup_time: setupTime || null, pack_down_time: packDownTime || null,
            lead_id: finalClientId, company_id: finalCompanyId || null, presentation_slide_content: { text: presentationContent }, packing_list: compiledPackingList,
            tech_notes: compiledTechNotes.trim(), is_template: isTemplate, custom_slides: customSlides, staffing_tier: calculated.staffing_tier, staff_count_required: calculated.staff_count_required,
            
            // ⚡ OVERWRITE THE DATABASE WITH ZEROS
            setup_fee: 0,
            travel_distance_km: 0,
            travel_rate_per_km: 0,
            logistics_hours: 0,
            logistics_hourly_rate: 0,
            
            // ⚡ THE FORMATTING FIX: Wrap the bundles in strings so Supabase doesn't reject them!
            logistics: JSON.stringify(packagedLogistics), 
            internal_costs: JSON.stringify(packagedInternalCosts),
            items: JSON.stringify(cleanedItems)
        };

        const { error: quoteError } = await supabase.from('quotes').upsert([quoteData]);
        if (quoteError) throw quoteError;

        const newQuote: any = {
            id: finalQuoteId, title: title || 'Untitled Quote', eventDate: eventDate || null, eventLocation, eventStartTime, eventEndTime, setupTime, packDownTime,
            client: { ...client, company: cleanCompanyName, companyId: finalCompanyId || undefined },
            items: cleanedItems, 
            logistics: packagedLogistics, 
            status: initialQuote?.status || 'DRAFT', createdAt: initialQuote?.createdAt || new Date().toISOString(), grandTotal, subtotal,
            attachments: attachments || [], linkedCrmItemId: finalClientId, lead_id: finalClientId, presentationSlideContent: presentationContent, packingList: compiledPackingList, 
            tech_notes: compiledTechNotes.trim(), is_template: isTemplate, custom_slides: customSlides, staffingTier: calculated.staffing_tier, staffCountRequired: calculated.staff_count_required, staff_count_required: calculated.staff_count_required,
            internal_costs: packagedInternalCosts,  
          };
          
          let acknowledged = false;
          const acknowledgeSaved = (savedQuote: Quote) => {
            acknowledged = true;
            draft.saved(submittedDraft, {
              currentQuoteId: finalQuoteId, currentLeadId: finalClientId,
              client: { ...submittedDraft.value.client, company: cleanCompanyName, companyId: finalCompanyId || submittedDraft.value.client.companyId },
            }, quoteDraftSource(savedQuote, linkedLead));
          };
          const result = await onSave(newQuote, shouldPreview, acknowledgeSaved);
          if (result === false) throw new Error('The full save did not complete. Your local draft is retained.');
          if (!acknowledged) acknowledgeSaved(newQuote);
          if (!shouldPreview) alert("Quote saved successfully!");
        
        } catch (error: any) {
            console.error("Critical Save Error:", error);
            alert(`Whoops! Save failed: ${error.message}`); 
        } finally {
            setIsSaving(false);
        }
  };

  return (
    <>
    <QuoteDraftNotice draft={draft} />
    {isSaving && <div role="status" className="fixed inset-0 z-[220] bg-slate-950/80 text-white p-8">Saving Quote — please wait. Your local draft is retained until the save completes.</div>}
    <fieldset disabled={isSaving || !!draft.pending || !!draft.navigation} className="min-w-0 border-0 p-0 m-0">
    <div style={isAiModalOpen?{display:'none'}:undefined} className="flex flex-col h-full animate-fade-in-up pb-12">
      {acceptedQuote && <p role="alert" className="p-4 text-amber-300">Accepted Quote: saved commercial totals are preserved. Calculations below are an unsaved preview only. Duplicate to a draft revision to change staffing or pricing.</p>}
      {!calculationReviewed && <p role="alert" className="p-4 text-amber-300">Legacy calculation review required: the saved staff total may already include line staffing. Choose the total or line-based mode below and review percentage-discount pricing. Nothing is corrected automatically.</p>}
      
      {/* ⚡ THE NEW CLEAN HEADER LAYOUT */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6 md:mb-8 bg-slate-900/50 p-4 rounded-[24px] border border-white/5">
        
        {/* LEFT SIDE: Title & Status Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full xl:w-auto">
            <h3 className="type-display text-2xl md:text-3xl text-white shrink-0 pl-2">
                {initialQuote ? 'Edit Quote' : 'New Quote Generator'}
            </h3>
            
            {currentQuoteId && (
                <div className="flex flex-wrap gap-2 sm:ml-2 sm:pl-4 sm:border-l border-white/10 pt-2 sm:pt-0 border-t sm:border-t-0">
                    <Button variant="outline" className="h-10 text-rose-400 border-rose-900/30 hover:bg-rose-900/20 text-xs px-3" onClick={() => handleStatusChange('REJECTED')}>
                        <ThumbsDown size={14} className="mr-1.5"/> Reject
                    </Button>
                    <Button variant="outline" className="h-10 text-brand-neonBlue border-brand-neonBlue/30 hover:bg-brand-neonBlue/10 text-xs px-3" onClick={() => handleStatusChange('SENT')}>
                        <Send size={14} className="mr-1.5"/> Mark Sent
                    </Button>
                    <Button variant="outline" className="h-10 text-emerald-400 border-emerald-900/30 hover:bg-emerald-400/20 text-xs px-3" onClick={() => handleStatusChange('APPROVED')}>
                        <ThumbsUp size={14} className="mr-1.5"/> Approve
                    </Button>
                </div>
            )}
        </div>

        {/* RIGHT SIDE: Templates & Save Actions */}
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 md:gap-3 w-full xl:w-auto pt-2 xl:pt-0 border-t border-white/5 xl:border-none mt-2 xl:mt-0">
            
            {/* Template Dropdown */}
            {baseTemplates.length > 0 && (
                <select 
                    onChange={(e) => {
                        if (e.target.value) {
                            handleLoadTemplate(e.target.value);
                            e.target.value = ""; 
                        }
                    }}
                    className="h-10 bg-brand-neonPink/10 text-brand-neonPink border border-brand-neonPink/30 rounded-xl px-4 text-[10px] font-bold uppercase tracking-widest outline-none cursor-pointer hover:bg-brand-neonPink/20 transition-colors shrink-0"
                >
                    <option value="">+ Load Template</option>
                    {baseTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.template_name}</option>
                    ))}
                </select>
            )}
            
            <Button variant="outline" className="h-10 hidden md:flex text-slate-400 border-white/10 hover:text-white text-xs px-3" onClick={() => setIsTemplateModalOpen(true)}>
                <Save size={14} className="mr-1.5" /> Save as Template
            </Button>

            <div className="h-6 w-px bg-white/10 hidden sm:block mx-1"></div>

            <Button variant="outline" className="h-10 text-xs px-4" onClick={cancel} disabled={isSaving}>Cancel</Button>
            
            <Button variant="outline" className="h-10 whitespace-nowrap text-xs px-4" onClick={() => handleSave(false)} disabled={isSaving}>
                {isSaving ? <Loader2 size={14} className="animate-spin inline mr-1.5"/> : <Save size={14} className="inline mr-1.5"/>} 
                Save Draft
            </Button>
            
            <Button className="h-10 bg-brand-neonBlue text-slate-950 font-bold whitespace-nowrap text-xs px-5 shadow-neon-blue-sm" onClick={() => handleSave(true)} disabled={isSaving}>
                {isSaving ? <><Loader2 size={14} className="animate-spin inline mr-1.5"/> Processing...</> : <><Eye size={14} className="inline mr-1.5"/> Save & Preview</>}
            </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         <div className="lg:col-span-2 space-y-6">
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 md:p-6 bg-slate-900/60 rounded-3xl border border-white/5 shadow-xl backdrop-blur-xl">
                <div className="flex-1">
                    <h4 className="type-heading text-white text-sm md:text-lg">Document Mode</h4>
                    <p className="text-slate-500 text-xs mt-1 font-medium leading-relaxed">Portfolios hide event logistics and grand totals, acting as a general service guide.</p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-white/5">
                    <button onClick={() => setIsTemplate(!isTemplate)} className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors shrink-0 ${isTemplate ? 'bg-brand-neonBlue' : 'bg-slate-700'}`}><span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${isTemplate ? 'translate-x-7' : 'translate-x-1'}`} /></button>
                    <span className={`text-xs font-bold tracking-widest uppercase whitespace-nowrap ${isTemplate ? 'text-brand-neonBlue' : 'text-slate-500'}`}>{isTemplate ? 'Portfolio Guide' : 'Formal Quote'}</span>
                </div>
            </div>

            <div className="p-5 md:p-6 bg-slate-900/40 rounded-3xl border border-brand-neonPink/20 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-neonPink/0 via-brand-neonPink to-brand-neonPink/0 opacity-20"></div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h3 className="type-heading text-lg text-white flex items-center gap-2"><Sparkles size={18} className="text-brand-neonPink" /> Custom Slides</h3>
                        <p className="text-slate-500 text-xs mt-1 font-medium">Paste GHL image URLs for custom Intro or Appendix slides.</p>
                    </div>
                    <Button size="sm" onClick={() => setCustomSlides([...customSlides, { url: '', placement: 'intro' }])} className="w-full sm:w-auto bg-brand-neonPink/10 text-brand-neonPink hover:bg-brand-neonPink hover:text-white rounded-xl border border-brand-neonPink/20 transition-all font-bold"><Plus size={14} className="mr-1 inline" /> Add Slide</Button>
                </div>
                <div className="space-y-3">
                    {customSlides.map((slide, index) => (
                        <div key={index} className="flex flex-col md:flex-row gap-3 items-start md:items-center bg-slate-950/80 p-3.5 rounded-2xl border border-white/5">
                            <div className="flex-1 w-full">
                                <input type="text" placeholder="Paste image URL here..." value={slide.url} onChange={e => { const newSlides = [...customSlides]; newSlides[index].url = e.target.value; setCustomSlides(newSlides); }} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-white text-sm focus:border-brand-neonPink outline-none transition-colors" />
                            </div>
                            <div className="w-full md:w-48">
                                <select value={slide.placement} onChange={e => { const newSlides = [...customSlides]; newSlides[index].placement = e.target.value; setCustomSlides(newSlides); }} className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-white text-sm focus:border-brand-neonPink outline-none cursor-pointer">
                                    <option value="intro">Cover / Intro</option>
                                    <option value="strategy">Post-Summary / Strategy</option>
                                    <option value="intermission">Intermission</option>
                                    <option value="appendix">Appendix / End</option>
                                </select>
                            </div>
                            <button onClick={() => { const newSlides = [...customSlides]; newSlides.splice(index, 1); setCustomSlides(newSlides); }} className="p-3 text-slate-500 hover:text-rose-400 transition-colors bg-slate-900 rounded-xl border border-slate-800 w-full md:w-auto flex justify-center"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
            </div>

             <GlassCard className="p-5 md:p-6 border-brand-neonBlue/20">
                 <h4 className="text-xs md:text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2"><User size={16} className="text-brand-neonBlue"/> Client & Event Details</h4>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-brand-neonBlue uppercase tracking-widest ml-1">Quote Title / Job Name *</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="e.g. Smith Wedding" value={title} onChange={e => setTitle(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Event Date *</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors [color-scheme:dark]" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
                    </div>
                 </div>
                 {/* ⚡ NEW: ROADSHOW / MULTI-DATE SCHEDULE */}
                    <div className="md:col-span-2">
                        <label className="text-[9px] text-slate-500 uppercase font-black mb-1.5 block">
                            Multi-Date / Roadshow Schedule (Optional)
                        </label>
                        <input 
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-brand-neonBlue transition-colors text-sm placeholder:text-slate-600" 
                            placeholder="e.g. Nov 12: AKL | Nov 15: WLG | Nov 20: CHC" 
                            value={logistics.roadshowSchedule || ''} 
                            onChange={e => setLogistics(prev => ({ ...prev, roadshowSchedule: e.target.value }))} 
                        />
                        <p className="text-[10px] text-slate-500 mt-1.5 italic">
                            If filled out, this overrides the single date display on the client's proposal. Keep the primary Event Date above set to the tour's start date.
                        </p>
                    </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pt-4 border-t border-white/5">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">First Name</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="e.g. Jane" value={client.firstName} onChange={e => setClient(prev => ({...prev, firstName: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Last Name</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="e.g. Doe" value={client.lastName} onChange={e => setClient(prev => ({...prev, lastName: e.target.value}))} />
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Contact Email</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors" type="email" placeholder="jane@example.com" value={client.email} onChange={e => setClient(prev => ({...prev, email: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Phone Number</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonBlue transition-colors" type="tel" placeholder="021 555 1234" value={client.phone} onChange={e => setClient(prev => ({...prev, phone: e.target.value}))} />
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/5">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Company / Organization</label>
                        <div className="relative group" ref={companyDropdownRef}>
                            <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input ref={companyInputRef} className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 pl-10 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="Search or type new..." value={companySearch} onChange={handleCompanyChange} onFocus={() => setShowCompanyDropdown(true)} />
                            {showCompanyDropdown && companySearch && (
                                <div className="absolute top-full left-0 w-full mt-2 bg-slate-800 border border-brand-neonBlue/30 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] z-20 max-h-48 overflow-y-auto overflow-x-hidden">
                                    {filteredCompanies.map(c => (
                                        <div key={c.id} className="px-4 py-3 hover:bg-brand-neonBlue hover:text-slate-900 cursor-pointer text-white text-sm font-medium border-b border-white/5 last:border-0 transition-colors" onClick={() => handleCompanySelect(c)}>{c.name}</div>
                                    ))}
                                    {filteredCompanies.length === 0 && (
                                        <div className="px-4 py-3 text-slate-400 text-sm italic">Type to add as new company...</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-brand-neonPink uppercase tracking-widest ml-1">Accounts / Billing Email</label>
                        <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonPink transition-colors" type="email" placeholder="invoices@company.com" value={client.accountsEmail || ''} onChange={e => setClient(prev => ({...prev, accountsEmail: e.target.value}))} />
                    </div>
                 </div>

                 <div className="grid grid-cols-1 gap-4 pt-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Event Venue</label>
                        <div className="relative">
                            <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3.5 pl-10 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="e.g. Hilton Hotel" value={eventLocation} onChange={e => setEventLocation(e.target.value)} />
                        </div>
                    </div>
                 </div>
             </GlassCard>

             <GlassCard className="p-6 border-brand-neonBlue/20">
                 <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-bold text-slate-400 uppercase flex items-center gap-2"><Sparkles size={16} className="text-brand-neonBlue"/> Executive Summary</h4>
                    
                    {/* ⚡ NEW AI GENERATE PITCH BUTTON */}
                    <button 
                        type="button"
                        onClick={() => setIsAiModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-neonBlue/10 text-brand-neonBlue border border-brand-neonBlue/30 rounded-lg hover:bg-brand-neonBlue hover:text-slate-900 transition-colors text-xs font-bold uppercase tracking-wider"
                    >
                        <Sparkles size={14} /> Draft summary
                    </button>
                 </div>
                 <div className="relative">
                    <textarea className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white text-sm outline-none focus:border-brand-neonBlue resize-y min-h-[120px] leading-relaxed transition-colors" placeholder="Write an introduction or use Draft summary." value={presentationContent} onChange={e => setPresentationContent(e.target.value)} />
                    <RewriteImprove label="Executive Summary" text={presentationContent} context={proposalRewriteContext(proposalContext({client,company:companySearch||client.company||'',title,date:eventDate,venue:eventLocation,lead:linkedLead,items,products:availableProducts}))} names={[title,eventLocation]} onApply={setPresentationContent}/>
                 </div>
             </GlassCard>

             <GlassCard className="p-5 md:p-6 space-y-6">
                 {!isTemplate && <div>
                     <h4 className="text-xs md:text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2">
                        <Clock size={16} className="text-slate-500"/> Event Logistics Timings
                     </h4>
                     <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1 uppercase tracking-wider">Pack In</label>
                            <select className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-brand-neonBlue appearance-none font-medium cursor-pointer transition-colors" value={setupTime} onChange={e => setSetupTime(e.target.value)}>
                                <option value="">--:--</option>
                                {getFilteredTimes().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-brand-neonBlue block mb-1 uppercase tracking-wider">Start Time</label>
                            <select className="w-full bg-brand-neonBlue/5 border border-brand-neonBlue/50 rounded-lg p-3 text-white outline-none focus:border-brand-neonBlue appearance-none font-bold cursor-pointer transition-colors" value={eventStartTime} onChange={e => setEventStartTime(e.target.value)}>
                                <option value="">--:--</option>
                                {getFilteredTimes(setupTime).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-brand-neonPink block mb-1 uppercase tracking-wider">End Time</label>
                            <select className="w-full bg-brand-neonPink/5 border border-brand-neonPink/50 rounded-lg p-3 text-white outline-none focus:border-brand-neonPink appearance-none font-bold cursor-pointer transition-colors" value={eventEndTime} onChange={e => setEventEndTime(e.target.value)}>
                                <option value="">--:--</option>
                                {getFilteredTimes(eventStartTime || setupTime).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1 uppercase tracking-wider">Pack Out</label>
                            <select className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:border-brand-neonBlue appearance-none font-medium cursor-pointer transition-colors" value={packDownTime} onChange={e => setPackDownTime(e.target.value)}>
                                <option value="">--:--</option>
                                {getFilteredTimes(eventEndTime || eventStartTime || setupTime).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                     </div>
                 </div>}

                 <div className="pt-5 border-t border-white/5 space-y-4">
                     <h4 className="text-xs md:text-sm font-bold text-brand-neonBlue uppercase tracking-wider flex items-center gap-2">
                        <Users size={16} className="text-brand-neonBlue"/> Quote Staffing Estimate
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div className="space-y-1.5">
                             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Staffing Tier Requirement</label>
                             <select 
                                 className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 text-sm text-white outline-none focus:border-brand-neonBlue transition-colors cursor-pointer"
                                 value={staffingTier}
                                 onChange={e => {
                                     const tier = e.target.value;
                                     setStaffingTier(tier);
                                     if (tier === 'DRY_HIRE') setStaffCountRequired(0);
                                 }}
                             >
                                 <option value="DRY_HIRE">📦 Dry Hire (No Staff)</option>
                                 <option value="SETUP_PACKOUT">🚚 Delivery, Setup & Pack-out</option>
                                 <option value="FULLY_STAFFED">👤 Fully Staffed Operations</option>
                             </select>
                         </div>
                         <div className="space-y-1.5">
                             <label htmlFor="quote-staffing-mode" className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Quote staffing calculation</label>
                             <select id="quote-staffing-mode" value={staffingMode} onChange={e => setStaffingMode(e.target.value as StaffingMode)} className="w-full bg-slate-900 rounded-xl p-3 text-white">
                               <option value="LINE_TOTAL">Use included line staff total</option>
                               <option value="EXPLICIT_TOTAL">Use explicit total (shared operators)</option>
                             </select>
                             <label htmlFor="quote-staffing-total" className="text-xs text-slate-400">Explicit total — replaces, never adds to line staff</label>
                             <input 
                                 id="quote-staffing-total"
                                 type="number" 
                                 min="0" 
                                 disabled={staffingMode === 'LINE_TOTAL'}
                                 className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 text-sm text-white outline-none focus:border-brand-neonBlue transition-colors disabled:opacity-30 disabled:cursor-not-allowed font-bold" 
                                 placeholder="Total operators needed..."
                                 value={staffCountRequired}
                                 onChange={e => setStaffCountRequired(parseInt(e.target.value) || 0)}
                             />
                             <p className="text-xs text-slate-300" data-testid="quote-staff-total">Quote staff total: {totals.dynamicStaffCount}. Quantity does not multiply staff. Shift Required Staff Total is unchanged.</p>
                             <label className="flex gap-2 text-xs text-slate-300"><input type="checkbox" checked={calculationReviewed} onChange={e => setCalculationReviewed(e.target.checked)} />I reviewed the staffing mode and percentage-discount totals for this draft.</label>
                         </div>
                     </div>
                 </div>
             </GlassCard>


             <GlassCard className="p-6">
                 <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-bold text-slate-400 uppercase flex items-center gap-2"><DollarSign size={16}/> Line Items</h4>
                    <Button size="sm" variant="outline" onClick={addItem}><Plus size={14} className="mr-1"/> Add Item</Button>
                 </div>
                 <div className="space-y-4">
                    {items.map((item, index) => (
                        <div key={item.id} className={`bg-slate-900 border ${item.isOptional ? 'border-brand-neonPink/50 bg-brand-neonPink/5' : 'border-white/5'} rounded-2xl p-6 relative group transition-all`}>
                        <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 items-start w-full">
                            <div className="flex-1 w-full space-y-2">
                                <input className={`w-full bg-transparent font-bold text-lg border-b border-transparent focus:border-slate-700 outline-none ${item.isOptional ? 'text-brand-neonPink' : 'text-white'}`} value={item.name} onChange={e => updateItem(item.id, { name: e.target.value })} />
                                <div className="relative">
                                    <textarea className="w-full bg-transparent text-slate-400 text-sm border-b border-transparent focus:border-slate-700 outline-none resize-none" rows={2} value={item.description} onChange={e => updateItem(item.id, { description: e.target.value })} />
                                    <RewriteImprove label={`${item.name} description`} text={item.description||''} context={proposalRewriteContext(proposalContext({client,company:companySearch||client.company||'',title,date:eventDate,venue:eventLocation,lead:linkedLead,items:[item],products:availableProducts}))} names={[item.name,title,eventLocation]} onApply={value=>updateItem(item.id,{description:value})}/>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 mt-2">
                                    <label className="flex items-center gap-2 text-[10px] text-slate-500 cursor-pointer uppercase font-bold tracking-widest hover:text-white transition-colors shrink-0">
                                        <input type="checkbox" checked={item.isOptional || false} onChange={(e) => updateItem(item.id, { isOptional: e.target.checked, isCoreChoice: false } as any)} className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-brand-neonPink focus:ring-brand-neonPink cursor-pointer"/>
                                        Optional Add-On
                                    </label>
                                    
                                    {item.isOptional && (
                                        <>
                                            <label className="flex items-center gap-2 text-[10px] text-brand-neonBlue cursor-pointer uppercase font-bold tracking-widest hover:text-white transition-colors shrink-0 ml-2 md:ml-4 border-l border-white/10 pl-2 md:pl-4 animate-fade-in">
                                                <input type="checkbox" checked={(item as any).isCoreChoice || false} onChange={(e) => updateItem(item.id, { isCoreChoice: e.target.checked } as any)} className="w-4 h-4 rounded border-brand-neonBlue/30 bg-slate-800 text-brand-neonBlue focus:ring-brand-neonBlue cursor-pointer"/>
                                                Set as "Core Experience" Choice
                                            </label>
                                            <label className="flex items-center gap-2 text-[10px] text-yellow-500 cursor-pointer uppercase font-bold tracking-widest hover:text-white transition-colors shrink-0 ml-2 md:ml-4 border-l border-white/10 pl-2 md:pl-4 animate-fade-in" title="Turn this on to see how it affects your profit margins below!">
                                                <input type="checkbox" checked={(item as any).simulateSelected || false} onChange={(e) => updateItem(item.id, { simulateSelected: e.target.checked } as any)} className="w-4 h-4 rounded border-yellow-500/30 bg-slate-800 text-yellow-500 focus:ring-yellow-500 cursor-pointer"/>
                                                Simulate in Profit Advisor
                                            </label>
                                        </>
                                    )}
                                    {item.isOptional && (initialQuote?.status === 'APPROVED' || (initialQuote?.status as string) === 'COMPLETED') && (
                                        (item as any).selected ? (
                                            <span className="bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-1 rounded flex items-center gap-1.5 text-[9px] uppercase font-black tracking-widest shadow-[0_0_10px_rgba(34,197,94,0.1)]"><Check size={12} strokeWidth={3} /> Client Accepted</span>
                                        ) : (
                                            <span className="bg-slate-800/80 text-slate-500 border border-white/5 px-2 py-1 rounded flex items-center gap-1.5 text-[9px] uppercase font-black tracking-widest"><X size={12} strokeWidth={3} /> Declined</span>
                                        )
                                    )}
                                </div>
                            </div>
                    
                            <div className="flex flex-wrap md:flex-nowrap gap-4 md:gap-6 p-4 md:p-5 bg-slate-950/50 rounded-2xl border border-white/5 w-full xl:w-auto mt-2 xl:mt-0">
                                <div className="w-20 border-r border-white/10 pr-4 md:pr-5 shrink-0">
                                    <label className="text-[9px] text-slate-500 uppercase font-black mb-1 block">Staff</label>
                                    <input 
                                        type="number" 
                                        min="0" 
                                        className="w-full bg-slate-800 rounded-lg p-2 text-white text-center outline-none border border-transparent focus:border-brand-neonBlue transition-all font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" 
                                        value={(item as any).staffRequired || ''} 
                                        onChange={e => updateItem(item.id, { staffRequired: parseInt(e.target.value) || 0 } as any)} 
                                        placeholder="0" 
                                    />
                                </div>
                                <div className="w-16">
                                    <label className="text-[9px] text-slate-500 uppercase font-black mb-1 block">Qty</label>
                                    <input type="number" className="w-full bg-slate-800 rounded-lg p-2 text-white text-center outline-none border border-transparent focus:border-brand-neonBlue transition-all font-bold" value={item.quantity === 0 ? '' : item.quantity} onChange={e => updateItem(item.id, { quantity: e.target.value as any })} />
                                </div>
                                <div className="w-24">
                                    <label className="text-[9px] text-slate-500 uppercase font-black mb-1 block">Unit Rate</label>
                                    <input type="number" className="w-full bg-slate-800 rounded-lg p-2 text-white text-right outline-none border border-transparent focus:border-brand-neonBlue transition-all font-bold" value={item.price === 0 ? '' : item.price} onChange={e => updateItem(item.id, { price: e.target.value as any })} />
                                </div>
                                <div className="w-20">
                                    <label className="text-[9px] text-brand-neonPink uppercase font-black mb-1 block text-center">Disc %</label>
                                    <input type="number" className="w-full bg-slate-800 rounded-lg p-2 text-brand-neonPink text-center outline-none border border-brand-neonPink/30 focus:border-brand-neonPink transition-all font-black" value={item.discount || ''} onChange={e => updateItem(item.id, { discount: e.target.value as any })} />
                                </div>
                                <div className="w-28 text-right border-l border-white/10 pl-6">
                                    <label className="text-[9px] text-slate-500 uppercase font-black mb-1 block">Row Total</label>
                                    <div className="type-numeric text-lg text-brand-neonBlue pt-1">${linePrice(item).toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="absolute top-4 right-4 flex items-center gap-2 bg-slate-950 p-1 rounded-lg border border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30 transition-colors" title="Move Up"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg></button>
                            <button onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1} className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30 transition-colors" title="Move Down"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></button>
                            <div className="w-px h-4 bg-white/10 mx-1"></div>
                            <button onClick={() => removeItem(item.id)} className="p-1.5 text-slate-500 hover:text-rose-500 transition-colors" title="Remove Item"><Trash2 size={14}/></button>
                        </div>
                    </div>
                    ))}
                 </div>
             </GlassCard>
         </div>

         <div className="space-y-6">
             <GlassCard className="p-4 bg-slate-900/50">
                <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center gap-2"><Box size={14}/> Add Product</h4>
                <div className="grid grid-cols-2 gap-2">
                    {availableProducts.map(p => (
                        <button key={p.id} onClick={() => addProductItem(p)} className="p-2 text-left bg-slate-800 hover:bg-slate-700 hover:border-brand-neonBlue/50 transition-all rounded border border-white/5 text-xs text-white truncate">{p.name}</button>
                    ))}
                </div>
             </GlassCard>

             <GlassCard className={`p-0 overflow-hidden transition-all duration-300 border ${showProfitAdvisor ? 'border-yellow-500/40 shadow-[0_0_20px_rgba(234,179,8,0.15)]' : 'border-white/5'}`}>
                 <div 
                    className="p-4 bg-black/40 flex justify-between items-center cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setShowProfitAdvisor(!showProfitAdvisor)}
                 >
                    <div className="flex items-center gap-2">
                        <Calculator size={16} className={showProfitAdvisor ? 'text-yellow-500' : 'text-slate-500'} />
                        <h4 className={`text-sm font-bold uppercase tracking-widest ${showProfitAdvisor ? 'text-yellow-500' : 'text-slate-400'}`}>Internal Profit Advisor</h4>
                    </div>
                    {showProfitAdvisor ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                 </div>

                 {showProfitAdvisor && (
                     <div className="p-6 bg-slate-900/80 border-t border-white/5 space-y-5 animate-fade-in">
                        <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                            <p className="text-xs text-yellow-500 font-medium leading-relaxed">
                                <AlertCircle size={14} className="inline mr-1.5 -mt-0.5" /> 
                                These metrics are strictly internal. Simulated optional add-ons affect only this advisor, not the Quote staff total or saved price.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="pb-4 border-b border-white/5">
                                <div className="flex justify-between items-start mb-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">1. Auto-Calculated Shift Labor</label>
                                    <span className="type-numeric text-sm text-white bg-slate-950 px-3 py-1 rounded-md border border-white/10 shadow-inner">
                                        ${totals.autoLaborCost.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                    <span>{totals.totalShiftHours.toFixed(1)} hrs × {totals.simulatedStaffCount} staff</span>
                                    <div className="flex items-center gap-2">
                                        <span>@ $</span>
                                        <input 
                                            type="number" 
                                            className="w-12 bg-slate-950 border border-slate-700 rounded p-1 text-center text-white outline-none focus:border-yellow-500"
                                            value={internalCosts.laborRatePerHour || ''}
                                            onChange={e => setInternalCosts(prev => ({...prev, laborRatePerHour: e.target.value as any}))}
                                        />
                                        <span>/hr</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">2. Design & Pre-Production</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-500 mr-2">$</span>
                                        <input className="type-numeric w-24 bg-slate-950 border border-slate-700 rounded-lg p-2 text-right text-white outline-none focus:border-yellow-500 text-sm" type="number" value={internalCosts.designPreProduction || ''} onChange={e => setInternalCosts(prev => ({...prev, designPreProduction: e.target.value as any}))} placeholder="0.00" />
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">3. Logistics & Travel (Gas, Hotel)</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-500 mr-2">$</span>
                                        <input className="type-numeric w-24 bg-slate-950 border border-slate-700 rounded-lg p-2 text-right text-white outline-none focus:border-yellow-500 text-sm" type="number" value={internalCosts.logisticsTravel || ''} onChange={e => setInternalCosts(prev => ({...prev, logisticsTravel: e.target.value as any}))} placeholder="0.00" />
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">4. Physical Materials (Paper, Props)</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-500 mr-2">$</span>
                                        <input className="type-numeric w-24 bg-slate-950 border border-slate-700 rounded-lg p-2 text-right text-white outline-none focus:border-yellow-500 text-sm" type="number" value={internalCosts.physicalMaterials || ''} onChange={e => setInternalCosts(prev => ({...prev, physicalMaterials: e.target.value as any}))} placeholder="0.00" />
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">5. Digital & Software (AI Tokens, Sub)</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-500 mr-2">$</span>
                                        <input className="type-numeric w-24 bg-slate-950 border border-slate-700 rounded-lg p-2 text-right text-white outline-none focus:border-yellow-500 text-sm" type="number" value={internalCosts.digitalSoftware || ''} onChange={e => setInternalCosts(prev => ({...prev, digitalSoftware: e.target.value as any}))} placeholder="0.00" />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
                                <div className="bg-black/40 p-4 rounded-xl border border-white/5">
                                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Gross Profit</span>
                                    <span className="type-numeric text-lg text-white">${totals.grossProfit.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className={`p-4 rounded-xl border ${totals.profitMarginPercent >= 45 ? 'bg-green-500/10 border-green-500/30' : totals.profitMarginPercent >= 30 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                    <span className={`text-[9px] font-black uppercase tracking-widest block mb-1 ${totals.profitMarginPercent >= 45 ? 'text-green-500' : totals.profitMarginPercent >= 30 ? 'text-yellow-500' : 'text-red-500'}`}>Margin Target</span>
                                    <span className={`type-numeric text-2xl ${totals.profitMarginPercent >= 45 ? 'text-green-400' : totals.profitMarginPercent >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {totals.profitMarginPercent.toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                     </div>
                 )}
             </GlassCard>

             <GlassCard className="p-6">
                 <h4 className="text-sm font-bold text-slate-400 uppercase mb-2 flex items-center gap-2"><Box size={16} className="text-brand-neonPink"/> Crew Packing List</h4>
                 <p className="text-[10px] text-slate-500 mb-4 leading-relaxed">Items auto-populate when saved. Add custom extras below.</p>
                 
                 <div className="flex flex-wrap gap-2 mb-4">
                     {packingList.map((item, index) => (
                         <span key={`${item}-${index}`} className="text-[10px] font-bold bg-slate-900 border border-slate-700 text-slate-300 pl-1 pr-2 py-1 rounded-full flex items-center gap-1 group transition-all hover:border-brand-neonPink">
                             <button onClick={() => moveGearItem(index, 'left')} disabled={index === 0} className="p-1 text-slate-600 hover:text-white disabled:opacity-20 transition-colors" title="Move Earlier"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
                             <span className="px-1">{item}</span>
                             <button onClick={() => moveGearItem(index, 'right')} disabled={index === packingList.length - 1} className="p-1 text-slate-600 hover:text-white disabled:opacity-20 transition-colors" title="Move Later"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
                             <div className="w-px h-3 bg-white/10 mx-0.5"></div>
                             <button onClick={() => setPackingList(prev => prev.filter(i => i !== item))} className="text-slate-600 hover:text-brand-neonPink p-1" title="Remove"><X size={12}/></button>
                         </span>
                     ))}
                     {packingList.length === 0 && (
                         <span className="text-[10px] text-slate-600 italic">No custom gear assigned yet.</span>
                     )}
                 </div>

                 <input className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-xs outline-none focus:border-brand-neonPink transition-colors placeholder:text-slate-600" placeholder="+ Type custom gear & press Enter" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const val = e.currentTarget.value.trim(); if (val && !packingList.includes(val)) { setPackingList(prev => [...prev, val]); } e.currentTarget.value = ''; } }} />
             </GlassCard>
             
             <GlassCard className="p-6 bg-gradient-to-br from-slate-900 to-slate-950">
                <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                    <div className="flex justify-between items-end">
                        <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Subtotal (Excl. GST)</span>
                        <span className="type-numeric text-3xl text-brand-neonBlue">${totals.subtotal.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-end">
                        <span className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">+ 15% GST</span>
                     <span className="type-numeric text-sm text-slate-400">${(totals.grandTotal - totals.subtotal).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                </div>
             </GlassCard>
         </div>
      </div>

      </div>
      {/* ⚡ NEW TEMPLATE SAVE MODAL */}
      <AnimatePresence>
        {isTemplateModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-lg bg-slate-900 border border-brand-neonPink/30 rounded-3xl p-6 shadow-[0_0_40px_rgba(244,63,94,0.15)] space-y-4"
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2 text-brand-neonPink">
                  <Save size={20} />
                  <h3 className="type-heading text-xl text-white">Save as Base Template</h3>
                </div>
                <button onClick={() => setIsTemplateModalOpen(false)} className="text-slate-400 hover:text-white p-1"><X size={18} /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Template Name *</label>
                  <input 
                    value={templateForm.name} onChange={e => setTemplateForm({...templateForm, name: e.target.value})}
                    placeholder="e.g., Air NZ - 4Hr Premium Booth"
                    className="w-full bg-slate-950 border border-white/10 rounded-xl p-3.5 text-white outline-none focus:border-brand-neonPink"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Description / Notes (Optional)</label>
                  <textarea 
                    value={templateForm.description} onChange={e => setTemplateForm({...templateForm, description: e.target.value})}
                    placeholder="e.g., Use this for high-tier corporate clients requiring unlimited prints."
                    className="w-full h-24 bg-slate-950 border border-white/10 rounded-xl p-3 text-sm text-white resize-none outline-none focus:border-brand-neonPink custom-scrollbar"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsTemplateModalOpen(false)} className="flex-1 border-white/10 text-slate-300 hover:bg-white/5" disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSaveAsBaseTemplate} className="flex-[2] bg-brand-neonPink text-white shadow-neon-pink border-none font-bold" disabled={isSaving}>
                  {isSaving ? 'Saving Template...' : 'Save to Library'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {isAiModalOpen&&hydratedSource===`${source}:${draft.reset}`&&<ConversationalSummary
        scopeKey={`${draftScope?draftKey(draftScope,requestedQuote?.id||currentQuoteId):'editor:'+conversationEditorId.current}:${currentLeadId||linkedLead?.id||'no-lead'}:${client.email.toLowerCase()}|${client.companyId||client.company||''}`}
        initialText={presentationContent}
        session={conversationSession} onSession={setConversationSession}
        context={summaryContext({client,company:companySearch||client.company||'',title,date:eventDate,roadshow:logistics.roadshowSchedule||'',venue:eventLocation,start:eventStartTime,end:eventEndTime,lead:linkedLead,items,products:availableProducts})}
        onGenerate={generateConversationalSummary}
        onApply={setPresentationContent}
        onClose={()=>setIsAiModalOpen(false)}
      />}
      
    </fieldset>
    </>
  );
};

export default QuoteBuilder;
